import { useEffect, useState } from "react";
import { supabase } from "../lib/supabase";
import { toOrgStartISO, toOrgEndISO } from "../lib/timezone";

// =============================================================================
// USE SETTER UNREPLIED HOOK
// =============================================================================
// PURPOSE: Live count of a single setter's unreplied conversations (lead sent
// the last message, setter never replied) for the ACTIVITY row on the setter
// scorecard. Modeled on useSetterProof.ts but scoped to one setter and one
// metric — no lazy fetchProof() trigger, no bucket cache, just a count.
//
// Calls the same get_setter_proof RPC as the proof modal
// (p_metric_type: 'unreplied'); count = the returned row array's length.
// =============================================================================

export interface UseSetterUnrepliedResult {
  count: number | null;
  loading: boolean;
  error: string | null;
}

export function useSetterUnreplied(
  organizationId: string | null,
  setterId: string | null,
  dateRange: { start: Date | null; end: Date | null },
  timezone?: string
): UseSetterUnrepliedResult {
  const [count, setCount] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!organizationId || !setterId || !supabase) return;
    if (!dateRange.start || !dateRange.end) return;

    let cancelled = false;
    setLoading(true);
    setError(null);
    // Clear the prior range's count so a slow/failed refetch can't leave a stale
    // number on screen (the scorecard checks count != null before loading/error).
    // During load count is null → the scorecard shows a dash; on error it stays
    // null → dash, never a plausible-looking wrong number for the new range.
    setCount(null);

    (async () => {
      try {
        const { data, error: rpcError } = await supabase!.rpc("get_setter_proof", {
          p_organization_id: organizationId,
          p_setter_ids: [setterId],
          p_start_date: toOrgStartISO(dateRange.start!, timezone),
          p_end_date: toOrgEndISO(dateRange.end!, timezone),
          p_metric_type: "unreplied",
        });

        if (cancelled) return;
        if (rpcError) throw rpcError;

        // RPC returns a JSONB array — Supabase client already parses it.
        setCount(Array.isArray(data) ? data.length : 0);
      } catch (err) {
        if (cancelled) return;
        console.warn("Failed to fetch unreplied count:", err);
        // Never fake a 0 on error — surface it so the scorecard can show a dash.
        setError(err instanceof Error ? err.message : "Failed to fetch unreplied count");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [organizationId, setterId, dateRange.start, dateRange.end, timezone]);

  return { count, loading, error };
}
